import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import { applyFlavor, resolveFlavor } from './config/flavors'
import { usePropertiesStore } from '@/stores/properties'
import { useAuthStore } from '@/stores/auth'
import { useAccountStore } from '@/stores/account'
import { setAccountSlug, getAccountSlug } from '@/services/httpClient'

const app = createApp(App)
const pinia = createPinia()
const flavor = resolveFlavor()

app.use(pinia)
app.use(router)

applyFlavor(flavor)
app.provide('flavor', flavor)

// ---------------------------------------------------------------------------
// Boot sequence — account & slug MUST resolve before the app mounts,
// otherwise component onMounted hooks fire API calls without X-Account-Slug.
//
//   1. Extract slug from URL path (shared domain)
//   2. Resolve account (sends slug to API)
//   3. Set slug on all Axios instances (X-Account-Slug header)
//   4. Mount the app (components can now safely call APIs)
//   5. Handle slug routing redirects
//   6. Fire background API calls (properties, auth session)
// ---------------------------------------------------------------------------
;(async () => {
  const accountStore = useAccountStore(pinia)
  const hostname = window.location.hostname
  const isShared = ['booking.gopgms.com', 'localhost', '127.0.0.1'].includes(hostname)

  // On the shared domain, extract the account slug from the URL path
  // e.g. /demo-prestige-pg/properties → slug = 'demo-prestige-pg'
  let urlSlug = null
  if (isShared) {
    const firstSegment = window.location.pathname.split('/').filter(Boolean)[0]
    // Only treat as slug if it doesn't match a known flat route
    const knownFlatPaths = ['explore', 'properties', 'property', 'about', 'contact', 'faq', 'privacy-policy', 'checkout', 'payment-status', 'auth']
    if (firstSegment && !knownFlatPaths.includes(firstSegment)) {
      urlSlug = firstSegment
    }
  }

  // 1. Resolve account — must complete before any other API call
  const accountData = await accountStore.resolve(undefined, urlSlug).catch(() => null)

  // 2. Set slug header for all HTTP clients
  //    Priority: store value → API response → URL path → flavor config
  const effectiveSlug =
    accountStore.accountSlug ||
    accountData?.accountSlug ||
    accountData?.account_slug ||
    accountData?.slug ||
    urlSlug ||
    flavor.slug

  // Always call setAccountSlug — even on whitelabel domains the API may return a slug
  setAccountSlug(effectiveSlug || null)

  // Ensure the store also holds the slug
  if (effectiveSlug && !accountStore.accountSlug) {
    accountStore.accountSlug = effectiveSlug
  }

  // Debug: verify slug is set (survives drop_console in production terser)
  if (typeof window !== 'undefined') {
    window.__gopgms_boot_debug = {
      hostname,
      isShared,
      urlSlug,
      effectiveSlug,
      accountSlug: getAccountSlug(),
      storeSlug: accountStore.accountSlug,
      accountData
    }
  }

  // 3. Mount the app — all component onMounted hooks will now see the slug
  app.mount('#app')

  // 4. On shared domain, handle slug routing
  if (isShared) {
    const currentRoute = router.currentRoute.value
    const hasSlugParam = currentRoute.params?.accountSlug

    if (!effectiveSlug && !hasSlugParam) {
      // Bare shared domain with no account resolved — show landing page
      if (currentRoute.name !== 'explore') {
        router.replace({ name: 'explore' })
      }
      return // No account context — skip property/auth APIs
    }

    if (effectiveSlug && !hasSlugParam) {
      // Account resolved but slug missing from URL — add it
      const targetPath = `/${effectiveSlug}${currentRoute.path === '/' ? '' : currentRoute.path}`
      router.replace({
        path: targetPath,
        query: currentRoute.query,
        hash: currentRoute.hash || undefined
      })
    }
  }

  // 5. Fire background API calls — they will include X-Account-Slug
  const propertiesStore = usePropertiesStore(pinia)
  propertiesStore.fetchProperties().catch(() => {
    // Static seed data is already loaded in the store.
  })

  const authStore = useAuthStore(pinia)

  // 5a. Check for SSO token in URL (cross-app login from management app)
  const urlParams = new URLSearchParams(window.location.search)
  const ssoToken = urlParams.get('sso_token')

  if (ssoToken) {
    // Validate SSO token and auto-login
    const ssoSuccess = await authStore.validateSsoToken(ssoToken).catch(() => false)

    // Clean URL by removing sso_token param (security: avoid token in history/logs)
    urlParams.delete('sso_token')
    const cleanSearch = urlParams.toString()
    const cleanUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '') + window.location.hash
    window.history.replaceState({}, '', cleanUrl)

    if (!ssoSuccess) {
      // SSO failed — fall back to normal session hydration
      authStore.hydrateSession()
    }
  } else {
    // No SSO token — normal session hydration from localStorage
    authStore.hydrateSession()
  }
})()
